import React from 'react';
import * as PropTypes from 'prop-types';
import { Button, Form, OverlayTrigger, Popover } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle } from '@whitebox-learning/fontawesome-pro-light-svg-icons';
import { Formik, Form as FormikForm } from 'formik';
import MenuTitle from './MenuTitle';
import { Loading } from '../Loading/Loading';
import ConceptNameInput from './ConceptNameInput';

const SaveConceptMenu = ({
  saveRevision,
  back,
  close,
  conceptName,
  concepts,
  saving,
  allowCompetition,
}) => {
  const enterCompRef = React.useRef(false); // For two submit buttons

  return (
    <div className="fm-simple-panel">
      <MenuTitle back={back} close={close}>
        Save
      </MenuTitle>
      <div className="fm-scroll">
        <Formik
          initialValues={{
            conceptName: conceptName,
            notes: '',
            gotoComp: false,
          }}
          onSubmit={async (values) => {
            const enterComp = enterCompRef.current;
            saveRevision(
              enterComp,
              values.notes,
              values.conceptName,
              enterComp && values.gotoComp
            );
          }}
        >
          {(props) => (
            <FormikForm>
              {conceptName || <ConceptNameInput existingConcepts={concepts} />}

              {/* Notes textarea */}
              <Form.Group className="mb-3" controlId="notes">
                <Form.Label className="me-2">Notes</Form.Label>
                <OverlayTrigger
                  placement="bottom"
                  overlay={
                    <Popover id="save-help" title="What's this?">
                      <Popover.Body>
                        <p>
                          Any notes you add will be captured in the Journal
                          (under Engineering Logs). Why did you make these
                          changes? What outcome do you expect?
                        </p>
                        <p>
                          These are <em>your</em> notes about <em>your</em>{' '}
                          design. There is no right or wrong answer, and all
                          entries are editable at any time in the Journal.
                        </p>
                      </Popover.Body>
                    </Popover>
                  }
                >
                  <Button
                    variant="link"
                    className="p-0 border-0 align-baseline"
                    aria-label="Help information about notes"
                  >
                    <FontAwesomeIcon icon={faQuestionCircle} />
                  </Button>
                </OverlayTrigger>

                <Form.Control
                  as="textarea"
                  rows={5}
                  name="notes"
                  placeholder="Notes..."
                  onChange={props.handleChange}
                  onBlur={props.handleBlur}
                  value={props.values.notes}
                  className={
                    props.touched.notes && props.errors.notes ? 'error' : null
                  }
                />
                <Form.Text>Optionally add notes about this revision</Form.Text>
              </Form.Group>

              <Button
                className="btn-block"
                variant="primary"
                onClick={() => {
                  enterCompRef.current = false;
                  props.submitForm();
                }}
              >
                Save a Working Copy
              </Button>

              {/* Go to copmetition checkbox  */}

              {!allowCompetition ? (
                <p>
                  <small className="text-danger">
                    Your teacher has disabled the competition.
                  </small>
                </p>
              ) : (
                <>
                  <div className="text-center my-1"> - OR -</div>

                  <Button
                    className="btn-block"
                    variant="primary"
                    onClick={() => {
                      enterCompRef.current = true;
                      props.submitForm();
                    }}
                  >
                    Save and Enter Competition
                  </Button>

                  <Form.Group>
                    <Form.Check
                      type="checkbox"
                      id="goto-comp-checkbox"
                      checked={props.values.gotoComp}
                      onChange={(e) =>
                        props.setFieldValue('gotoComp', e.target.checked)
                      }
                      label="and go to the competition setup"
                    />
                  </Form.Group>
                </>
              )}
            </FormikForm>
          )}
        </Formik>
        {saving && <Loading />}
      </div>
    </div>
  );
};

SaveConceptMenu.propTypes = {
  saveRevision: PropTypes.func.isRequired,
  back: PropTypes.func.isRequired,
  close: PropTypes.func.isRequired,
  conceptName: PropTypes.string,
  concepts: PropTypes.array,
  saving: PropTypes.bool,
  allowCompetition: PropTypes.bool,
};

SaveConceptMenu.defaultProps = {
  conceptName: null,
  saving: false,
  allowCompetition: true,
  concepts: [],
};

export default SaveConceptMenu;
